/**
 * React Query hooks for Team management.
 * Provides data fetching, caching, and mutations for the RBAC system.
 */

import {
    teamsApi,
    type InviteMemberRequest,
    type TeamRole,
} from '@/api/teams'
import { useTeamStore } from '@/store/team'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// =============================================================================
// Query Keys
// =============================================================================

export const teamKeys = {
  all: ['teams'] as const,
  lists: () => [...teamKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...teamKeys.lists(), filters] as const,
  details: () => [...teamKeys.all, 'detail'] as const,
  detail: (id: string) => [...teamKeys.details(), id] as const,
  members: (teamId: string) => [...teamKeys.all, 'members', teamId] as const,
}

// =============================================================================
// Team Queries
// =============================================================================

/**
 * Fetch all teams for the current user.
 *
 * NOTE: Do NOT call setTeams() inside queryFn — store updates inside queryFn
 * trigger Zustand subscribers which re-render components, and in combination
 * with Suspense boundaries this can cause React Query observers to be
 * destroyed/recreated → infinite fetch loop.
 *
 * The TeamInitializer component is responsible for populating the team store.
 * This hook is purely for React Query data access.
 */
export function useTeams() {
  return useQuery({
    queryKey: teamKeys.lists(),
    queryFn: () => teamsApi.listTeams(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch a single team by ID.
 */
export function useTeam(teamId: string) {
  return useQuery({
    queryKey: teamKeys.detail(teamId),
    queryFn: () => teamsApi.getTeam(teamId),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch team members and pending invitations.
 */
export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: teamKeys.members(teamId),
    queryFn: () => teamsApi.listMembers(teamId),
    enabled: !!teamId,
    staleTime: 30 * 1000, // 30 seconds
  })
}

// =============================================================================
// Team Mutations
// =============================================================================

/**
 * Create a new team.
 */
export function useCreateTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => teamsApi.createTeam({ name }),
    onSuccess: (newTeam) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
      return newTeam
    },
  })
}

/**
 * Update team settings.
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient()
  const setTeams = useTeamStore((s) => s.setTeams)
  const setCurrentTeam = useTeamStore((s) => s.setCurrentTeam)
  const currentTeamId = useTeamStore((s) => s.currentTeamId)

  return useMutation({
    mutationFn: ({ teamId, name }: { teamId: string; name: string }) =>
      teamsApi.updateTeam(teamId, { name }),
    onSuccess: async (updatedTeam) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.detail(updatedTeam.id) })
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
      // Sync fresh teams list to Zustand store
      const teams = await teamsApi.listTeams()
      setTeams(teams)
      // Update current team if it was the one renamed
      if (updatedTeam.id === currentTeamId) {
        const current = teams.find((t) => t.id === updatedTeam.id)
        if (current) setCurrentTeam(current, current.myRole)
      }
    },
  })
}

/**
 * Delete a team.
 */
export function useDeleteTeam() {
  const queryClient = useQueryClient()
  const setTeams = useTeamStore((s) => s.setTeams)
  const setCurrentTeam = useTeamStore((s) => s.setCurrentTeam)
  const clearTeamContext = useTeamStore((s) => s.clearTeamContext)
  const currentTeamId = useTeamStore((s) => s.currentTeamId)

  return useMutation({
    mutationFn: (teamId: string) => teamsApi.deleteTeam(teamId),
    onSuccess: async (_data, deletedTeamId) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
      // Sync fresh teams list to Zustand store
      const teams = await teamsApi.listTeams()
      setTeams(teams)
      // If the deleted team was the current one, switch to another or clear
      if (deletedTeamId === currentTeamId) {
        if (teams.length > 0) {
          const next = teams.find((t) => t.isDefault) ?? teams[0]
          if (next) {
            setCurrentTeam(next, next.myRole)
          } else {
            clearTeamContext()
          }
        } else {
          clearTeamContext()
        }
      }
    },
  })
}

// =============================================================================
// Member Mutations
// =============================================================================

/**
 * Invite a new member to the team.
 */
export function useInviteMember() {
  const queryClient = useQueryClient()
  const currentTeamId = useTeamStore((state) => state.currentTeamId)

  return useMutation({
    mutationFn: (data: InviteMemberRequest) => {
      if (!currentTeamId) throw new Error('No team selected')
      return teamsApi.inviteMember(currentTeamId, data)
    },
    onSuccess: () => {
      if (currentTeamId) {
        queryClient.invalidateQueries({ queryKey: teamKeys.members(currentTeamId) })
      }
    },
  })
}

/**
 * Update a member's role.
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient()
  const currentTeamId = useTeamStore((state) => state.currentTeamId)

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: TeamRole }) => {
      if (!currentTeamId) throw new Error('No team selected')
      return teamsApi.updateMemberRole(currentTeamId, userId, role)
    },
    onSuccess: () => {
      if (currentTeamId) {
        queryClient.invalidateQueries({ queryKey: teamKeys.members(currentTeamId) })
      }
    },
  })
}

/**
 * Remove a member from the team.
 */
export function useRemoveMember() {
  const queryClient = useQueryClient()
  const currentTeamId = useTeamStore((state) => state.currentTeamId)

  return useMutation({
    mutationFn: (userId: string) => {
      if (!currentTeamId) throw new Error('No team selected')
      return teamsApi.removeMember(currentTeamId, userId)
    },
    onSuccess: () => {
      if (currentTeamId) {
        queryClient.invalidateQueries({ queryKey: teamKeys.members(currentTeamId) })
      }
    },
  })
}

// =============================================================================
// Invitation Mutations
// =============================================================================

/**
 * Cancel a pending invitation.
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient()
  const currentTeamId = useTeamStore((state) => state.currentTeamId)

  return useMutation({
    mutationFn: (invitationId: string) => {
      if (!currentTeamId) throw new Error('No team selected')
      return teamsApi.cancelInvitation(currentTeamId, invitationId)
    },
    onSuccess: () => {
      if (currentTeamId) {
        queryClient.invalidateQueries({ queryKey: teamKeys.members(currentTeamId) })
      }
    },
  })
}

/**
 * Resend an invitation email.
 */
export function useResendInvitation() {
  const currentTeamId = useTeamStore((state) => state.currentTeamId)

  return useMutation({
    mutationFn: (invitationId: string) => {
      if (!currentTeamId) throw new Error('No team selected')
      return teamsApi.resendInvitation(currentTeamId, invitationId)
    },
  })
}

/**
 * Accept an invitation (used on accept page).
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient()
  const setTeams = useTeamStore((s) => s.setTeams)

  return useMutation({
    mutationFn: (token: string) => teamsApi.acceptInvitation(token),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
      const teams = await teamsApi.listTeams()
      setTeams(teams)
    },
  })
}

// =============================================================================
// Team Context Mutations
// =============================================================================

/**
 * Switch the current team context.
 */
export function useSwitchTeam() {
  const queryClient = useQueryClient()
  const setCurrentTeam = useTeamStore((s) => s.setCurrentTeam)
  const teams = useTeamStore((s) => s.teams)

  return useMutation({
    mutationFn: (teamId: string) => teamsApi.switchTeam(teamId),
    onSuccess: (team) => {
      // Find the enriched team with role info
      const enriched = teams.find((t) => t.id === team.id)
      const role = enriched?.myRole || 'member'
      setCurrentTeam(team, role)
      // Only invalidate the teams list — not detail/members queries
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() })
    },
  })
}

// =============================================================================
// Convenience Hooks
// =============================================================================

/**
 * Get current team context from store.
 */
export function useCurrentTeam() {
  return useTeamStore((state) => ({
    team: state.currentTeam,
    teamId: state.currentTeamId,
    role: state.currentRole,
    hasPermission: state.hasPermission,
    canManageTeam: state.canManageTeam,
    canInviteMembers: state.canInviteMembers,
    canManageMembers: state.canManageMembers,
    canEditResources: state.canEditResources,
    isOwner: state.isOwner,
  }))
}
